"use client"

import { useState } from "react"
import {
  Container, Stack, Title, Text, Card, Group, Box, Badge, Progress, Button,
  TextInput, Select, Grid, Divider, Checkbox, Anchor, Alert
} from "@mantine/core"
import { TbArrowRight, TbCheck, TbInfoCircle } from "react-icons/tb"
import type { ProfileWizardProps, ProfileWizardData, WizardOption } from "./types"

function SelectionCard({ option, onClick, accentColor = "green" }: {
  option: WizardOption
  onClick: () => void
  accentColor?: string
}) {
  const Icon = option.icon
  return (
    <Card
      padding="md"
      radius="md"
      className={`cursor-pointer transition-all hover:scale-102 hover:shadow-lg border-2 border-transparent hover:border-${accentColor}-500`}
      onClick={onClick}
    >
      <Group gap="md" wrap="nowrap">
        <Box className={`p-3 rounded-lg bg-linear-to-br from-${accentColor}-400 to-teal-500`}>
          <Icon size={32} className="text-white" />
        </Box>
        <div className="flex-1">
          <div className="text-base font-semibold">{option.title}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{option.description}</div>
        </div>
        <TbArrowRight size={20} className="text-gray-400" />
      </Group>
    </Card>
  )
}

export function ProfileWizard({
  userTypeOptions,
  roleOptions,
  countries,
  fetchRegions,
  translations: t,
  individualTypeValue,
  companyTypeValue,
  onComplete,
  loading = false,
  userName = "",
  preSelectedRole = ""
}: ProfileWizardProps) {
  const [activeStep, setActiveStep] = useState(0)
  const [regions, setRegions] = useState<Array<{ value: string; label: string }>>([])
  const [regionsLoading, setRegionsLoading] = useState(false)

  const [formData, setFormData] = useState<ProfileWizardData>({
    userType: "",
    organizationRole: preSelectedRole,
    organizationName: "",
    country: "",
    region: "",
    municipality: "",
    description: "",
    vatNumber: "",
    street: "",
    city: "",
    postalCode: "",
    acceptedCarbonomexTerms: false,
    acceptedForestSyncTerms: false,
  })

  const updateFormData = (data: Partial<ProfileWizardData>) => {
    setFormData(prev => ({ ...prev, ...data }))
  }

  const handleCountryChange = async (value: string | null) => {
    updateFormData({ country: value || "", region: "" })
    setRegions([])
    if (value) {
      setRegionsLoading(true)
      const options = await fetchRegions(value)
      setRegions(options)
      setRegionsLoading(false)
    }
  }

  const handleUserTypeSelect = (type: string) => {
    updateFormData({ userType: type })
    // When role is pre-selected by intent, skip the role step
    if (preSelectedRole) {
      setActiveStep(2)
    } else {
      setActiveStep(1)
    }
  }

  const handleRoleSelect = (role: string) => {
    updateFormData({ organizationRole: role })
    setActiveStep(2)
  }

  const handleSubmit = () => {
    onComplete(formData)
  }

  const totalSteps = preSelectedRole ? 2 : 3
  const displayStep = preSelectedRole
    ? (activeStep === 0 ? 1 : 2)
    : activeStep + 1
  // Progress: show ≤80% on the last step until submitted, so the bar never hits 100% on an empty form
  const progressPercentage = Math.min(((displayStep - 0.25) / totalSteps) * 100, 90)
  const isIndividual = formData.userType === individualTypeValue

  return (
    <Container size="md" className="py-8">
      <Stack gap="md">
        <div className="text-center">
          <Title order={1} className="text-2xl font-bold mb-2">
            {t.title}
          </Title>
          <Text size="sm" c="dimmed">
            {t.subtitle}
          </Text>
        </div>

        <Card shadow="sm" p="sm" radius="md">
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" fw={600}>
                {t.step} {displayStep} {t.of} {totalSteps}
              </Text>
              <Badge variant="light" color="green" size="sm">
                {progressPercentage.toFixed(0)}% {t.complete}
              </Badge>
            </Group>
            <Progress value={progressPercentage} size="md" radius="xl" />
          </Stack>
        </Card>

        <Card shadow="md" p="lg" radius="md">
          {activeStep === 0 && (
            <Stack gap="sm">
              {userTypeOptions.map((option) => (
                <SelectionCard
                  key={option.value}
                  option={option}
                  onClick={() => handleUserTypeSelect(option.value)}
                  accentColor="green"
                />
              ))}
            </Stack>
          )}

          {activeStep === 1 && (
            <Stack gap="sm">
              {roleOptions.map((option) => (
                <SelectionCard
                  key={option.value}
                  option={option}
                  onClick={() => handleRoleSelect(option.value)}
                  accentColor="teal"
                />
              ))}
              <Group justify="flex-start" mt="md">
                <Button variant="light" size="md" onClick={() => setActiveStep(0)}>
                  {t.back}
                </Button>
              </Group>
            </Stack>
          )}

          {activeStep === 2 && (
            <Stack gap="md">
              {isIndividual ? (
                <Grid gutter="md">
                  <Grid.Col span={12}>
                    <TextInput
                      label={t.fullName}
                      placeholder={t.enterFullName}
                      required
                      size="md"
                      value={formData.organizationName}
                      onChange={(e) => updateFormData({ organizationName: e.target.value })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Select
                      label={t.country}
                      placeholder={t.selectCountry}
                      required
                      searchable
                      size="md"
                      data={countries}
                      value={formData.country}
                      onChange={handleCountryChange}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Select
                      label={t.region}
                      placeholder={regionsLoading ? t.loadingRegions : !formData.country ? t.selectCountryFirst : t.selectRegion}
                      searchable
                      size="md"
                      data={regions}
                      value={formData.region || null}
                      onChange={(value) => updateFormData({ region: value || "" })}
                      disabled={!formData.country || regionsLoading}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label={t.municipality}
                      placeholder={t.enterMunicipality}
                      size="md"
                      value={formData.municipality}
                      onChange={(e) => updateFormData({ municipality: e.target.value })}
                    />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <TextInput
                      label={t.description}
                      placeholder={t.enterDescription}
                      size="md"
                      value={formData.description}
                      onChange={(e) => updateFormData({ description: e.target.value })}
                      description={t.descriptionOptional}
                    />
                  </Grid.Col>
                </Grid>
              ) : (
                <Grid gutter="md">
                  <Grid.Col span={12}>
                    <TextInput
                      label={t.organizationName}
                      placeholder={t.enterOrgName}
                      required
                      size="md"
                      value={formData.organizationName}
                      onChange={(e) => updateFormData({ organizationName: e.target.value })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Select
                      label={t.country}
                      placeholder={t.selectCountry}
                      required
                      searchable
                      size="md"
                      data={countries}
                      value={formData.country}
                      onChange={handleCountryChange}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Select
                      label={t.region}
                      placeholder={regionsLoading ? t.loadingRegions : !formData.country ? t.selectCountryFirst : t.selectRegion}
                      searchable
                      size="md"
                      data={regions}
                      value={formData.region || null}
                      onChange={(value) => updateFormData({ region: value || "" })}
                      disabled={!formData.country || regionsLoading}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label={t.municipality}
                      placeholder={t.enterMunicipality}
                      size="md"
                      value={formData.municipality}
                      onChange={(e) => updateFormData({ municipality: e.target.value })}
                    />
                  </Grid.Col>
                  {formData.userType === companyTypeValue && (
                    <Grid.Col span={12}>
                      <TextInput
                        label={t.vatNumber}
                        placeholder={t.enterVatNumber}
                        size="md"
                        value={formData.vatNumber}
                        onChange={(e) => updateFormData({ vatNumber: e.target.value })}
                        description={t.vatOptional}
                      />
                    </Grid.Col>
                  )}
                  <Grid.Col span={12}>
                    <TextInput
                      label={t.description}
                      placeholder={t.enterDescription}
                      size="md"
                      value={formData.description}
                      onChange={(e) => updateFormData({ description: e.target.value })}
                    />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Divider label={t.legalAddress} labelPosition="center" />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <TextInput
                      label={t.street}
                      placeholder={t.enterStreet}
                      size="md"
                      value={formData.street}
                      onChange={(e) => updateFormData({ street: e.target.value })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 8 }}>
                    <TextInput
                      label={t.city}
                      placeholder={t.enterCity}
                      size="md"
                      value={formData.city}
                      onChange={(e) => updateFormData({ city: e.target.value })}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <TextInput
                      label={t.postalCode}
                      placeholder={t.enterPostalCode}
                      size="md"
                      value={formData.postalCode}
                      onChange={(e) => updateFormData({ postalCode: e.target.value })}
                    />
                  </Grid.Col>
                </Grid>
              )}

              <Stack gap="sm" mt="lg">
                <Alert icon={<TbInfoCircle size={16} />} color="blue" variant="light">
                  <Text size="xs" c="dimmed">{t.termsNotice}</Text>
                </Alert>
                <Checkbox
                  color="green"
                  size="md"
                  styles={{ input: { borderWidth: 2 } }}
                  checked={formData.acceptedCarbonomexTerms}
                  onChange={(e) => updateFormData({ acceptedCarbonomexTerms: e.currentTarget.checked })}
                  label={
                    <Text size="sm">
                      <Text component="span" c="red">* </Text>
                      I confirm that I am an authorised representative of the organisation named above,
                      that I am at least 18 years of age, and that I have read and agree to the{" "}
                      <Anchor href={t.termsLinkCarbonomex} target="_blank" size="sm">{t.acceptCarbonomexTerms}</Anchor>
                      {" "}and{" "}
                      <Anchor href={t.privacyLink} target="_blank" size="sm">Privacy Policy</Anchor>.
                    </Text>
                  }
                />
                <Checkbox
                  color="green"
                  size="md"
                  styles={{ input: { borderWidth: 2 } }}
                  checked={formData.acceptedForestSyncTerms}
                  onChange={(e) => updateFormData({ acceptedForestSyncTerms: e.currentTarget.checked })}
                  label={
                    <Text size="sm">
                      <Text component="span" c="red">* </Text>
                      I acknowledge that activity on Carbonomex may result in public records on the ForestSync Registry,
                      subject to the{" "}
                      <Anchor href={t.termsLinkForestSync} target="_blank" size="sm">{t.acceptForestSyncTerms}</Anchor>.
                      I accept these terms.
                    </Text>
                  }
                />
              </Stack>

              <Group justify="space-between" mt="md">
                <Button variant="light" size="md" onClick={() => setActiveStep(preSelectedRole ? 0 : 1)}>
                  {t.back}
                </Button>
                <Button
                  size="md"
                  onClick={handleSubmit}
                  loading={loading}
                  rightSection={<TbCheck size={18} />}
                  disabled={!formData.acceptedCarbonomexTerms || !formData.acceptedForestSyncTerms}
                  className="bg-linear-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
                >
                  {loading ? t.submitting : t.submit}
                </Button>
              </Group>
            </Stack>
          )}
        </Card>
      </Stack>
    </Container>
  )
}
